import React, { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import Pagination from "../../components/Panigation";
import { useLazyGetTransactionListQuery } from "../../services/transactions";
import { showError } from "../../helpers";
import type { TransactionItem } from "../../types/Transaction";
import { isValidInput } from "../../utils/validations";
import ReportsTopbar from "./reportsTopbar";
import { DateRangeType } from "./types";

export default function DailyPointsSummaryTable() {
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [dateRange, setDateRange] = useState<DateRangeType | null>(null);
  const [dateRange1, setDateRange2] = useState<DateRangeType | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [list, setList] = useState<TransactionItem[]>([]);
  const [selectRange, setSelectRange] = useState("default");

  const [getTransactionsData] = useLazyGetTransactionListQuery();

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const totalPages = Math.max(
    1,
    Math.ceil((totalCount || 0) / (pageSize || 10))
  );

  const fetchList = useCallback(async () => {
    try {
      const params: any = {
        page: page,
        limit: pageSize,
        search: debouncedSearchTerm,
        dateFrom: dateRange ? dayjs(dateRange).toISOString() : "",
        dateTo: dateRange1 ? dayjs(dateRange1).toISOString() : "",
      };
      const response = await getTransactionsData(params).unwrap();
      if (response?.statusCode === 200) {
        const data = response?.data || { transactionData: [], count: 0 };
        setList(data?.transactionData || []);
        setTotalCount(data?.count || 0);
      }
    } catch (error: unknown) {
      const apiMessage = (error as { data?: { message?: string } })?.data
        ?.message;
      showError(apiMessage || "Failed to fetch Transactions");
    }
  }, [
    page,
    pageSize,
    debouncedSearchTerm,
    getTransactionsData,
    dateRange,
    dateRange1,
  ]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    const today = dayjs();

    switch (selectRange) {
      case "1": // Day
        const todayStr = today.format("YYYY-MM-DD");
        setDateRange(todayStr);
        setDateRange2(todayStr);
        break;
      case "2": // Week
        const weekAgo = today.subtract(1, "week").format("YYYY-MM-DD");
        setDateRange(weekAgo);
        setDateRange2(today.format("YYYY-MM-DD"));
        break;
      case "3": // Month
        const monthAgo = today.subtract(1, "month").format("YYYY-MM-DD");
        setDateRange(monthAgo);
        setDateRange2(today.format("YYYY-MM-DD"));
        break;
      default:
        // For 'default' or any other value, clear the dates
        setDateRange(null);
        setDateRange2(null);
    }
  }, [selectRange]);

  return (
    <>
      <ReportsTopbar
        page={page}
        pageSize={pageSize}
        setDateRange={setDateRange}
        setDateRange2={setDateRange2}
        setSelectRange={setSelectRange}
        type="Transactions"
      />
      <div className="table_header">
        <div className="left_s">
          <SearchBar
            searchTerm={searchTerm}
            placeholder="Search..."
            setDebouncedSearchTerm={setDebouncedSearchTerm}
            value={searchTerm}
            onCross={() => setSearchTerm("")}
            onChange={(val: any) => {
              if (isValidInput(val.target.value)) {
                setSearchTerm(val.target.value);
              }
            }}
          />
        </div>
      </div>
      <TableContainer className="table_container">
        <Table>
          <TableHead>
            <TableRow hover>
              <TableCell>Name</TableCell>
              <TableCell>Membership account</TableCell>
              <TableCell>Membership card</TableCell>
              <TableCell>date</TableCell>
              <TableCell>Remaning Redeemed Points</TableCell>
              <TableCell>Earned Points</TableCell>
              <TableCell>In-Process Points</TableCell>
              <TableCell>Expired Points</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(list || []).length === 0 ? (
              <TableRow hover>
                <TableCell colSpan={8} align="center">
                  No data found
                </TableCell>
              </TableRow>
            ) : (
              (list || []).map((tx: TransactionItem) => {
                const user = tx?.userDetails || ({} as any);
                const earnedPoints =
                  tx?.entryType === "Sales" && tx?.points > 0 ? tx.points : 0;
                const expiredPoints = tx?.expiredPoints || 0;
                const redeemablePoints =
                  user?.transactionDetails?.redeemablePoints || 0;
                // In-Process Points: points that are not yet redeemable (pointsRedeemableFrom is in the future)
                const inProcessPoints =
                  tx?.pointsRedeemableFrom &&
                  new Date(tx.pointsRedeemableFrom) > new Date()
                    ? tx?.points > 0
                      ? tx.points
                      : 0
                    : 0;
                const purchaseDate = tx?.purchaseDate
                  ? new Date(tx.purchaseDate).toLocaleDateString()
                  : tx?.createdAt
                  ? new Date(tx.createdAt).toLocaleDateString()
                  : "-";

                return (
                  <TableRow hover key={tx?._id}>
                    <TableCell>
                      <Box
                        component="a"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate("/manage-users/detail/" + tx?.userId);
                        }}
                        className="user_block click_action"
                      >
                        <p>
                          <b>{user?.fullName || "-"}</b>
                          <span>{user?.email || ""}</span>
                          <span>
                            {`${user?.dialCode || ""} ${
                              user?.phoneNo || ""
                            }`.trim()}
                          </span>
                        </p>
                      </Box>
                    </TableCell>
                    <TableCell>{user?.memberAccount || "-"}</TableCell>
                    <TableCell>{user?.membershipCard || "-"}</TableCell>
                    <TableCell>{purchaseDate}</TableCell>
                    <TableCell>{redeemablePoints}</TableCell>
                    <TableCell className="c_success">{earnedPoints}</TableCell>
                    <TableCell className="c_warning">
                      {inProcessPoints}
                    </TableCell>
                    <TableCell className="c_danger">{expiredPoints}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination
        module={list || []}
        page={page}
        setPage={setPage}
        limit={pageSize}
        setLimit={setPageSize}
        onPageChange={onPageChange}
        totalPages={totalPages}
      />
    </>
  );
}
